import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import { BubbleMenuPlugin } from '@tiptap/extension-bubble-menu';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import Image from '@tiptap/extension-image';
import Youtube from '@tiptap/extension-youtube';
import CharacterCount from '@tiptap/extension-character-count';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Mention from '@tiptap/extension-mention';
import { createLowlight, common } from 'lowlight';
import axios from 'axios';
import { Icon } from './ui';
import { API_URL } from '../config/api';
import { useToast } from '../contexts/ToastContext';
import { SlashCommand } from './TipTapEditor/SlashCommand';
import { EmojiPicker } from './TipTapEditor/EmojiPicker';
import { mentionSuggestion } from './TipTapEditor/mentionSuggestion';
import 'tippy.js/dist/tippy.css';
import './TipTapEditor.css';

const lowlight = createLowlight(common);

/** A quote whose public link can be inserted into the body via a toolbar button. */
export interface QuoteLink {
  url: string;
  label?: string;
}

interface TipTapEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: number;
  showCharCount?: boolean;
  showSavedIndicator?: boolean;
  savedAt?: Date | null;
  /** When provided, a "קישור להצעה" toolbar button inserts this quote link. */
  quoteLink?: QuoteLink;
}

// ─── Auth helpers ──────────────────────────────────────────────────────────
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Upload image to DO Spaces via server
async function uploadImageToServer(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('image', file);
  const res = await axios.post(`${API_URL}/api/editor/upload-image`, formData, {
    headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
  });
  return res.data.url;
}

// Fallback: base64 (used only if upload fails)
const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// ─── Toolbar button ────────────────────────────────────────────────────────
interface TbButtonProps {
  icon: string;
  title: string;
  onClick: (e: React.MouseEvent) => void;
  active?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
}

const TbButton: React.FC<TbButtonProps> = ({ icon, title, onClick, active, disabled, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={[
      'tt-btn',
      active ? 'tt-btn-active' : '',
      disabled ? 'tt-btn-disabled' : '',
    ].filter(Boolean).join(' ')}
    title={title}
    aria-label={title}
    aria-pressed={active}
  >
    {children || <Icon name={icon} size={18} />}
  </button>
);

const TbDivider: React.FC = () => <span className="tt-divider" aria-hidden="true" />;

// ─── Link dialog ───────────────────────────────────────────────────────────
const LinkDialog: React.FC<{
  open: boolean;
  initialUrl?: string;
  onClose: () => void;
  onSave: (url: string) => void;
}> = ({ open, initialUrl = '', onClose, onSave }) => {
  const [url, setUrl] = useState(initialUrl);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setUrl(initialUrl);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, initialUrl]);

  if (!open) return null;

  const handleSave = () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    onSave(withProtocol);
    onClose();
  };

  return createPortal(
    <div className="tt-dialog-overlay" onClick={onClose}>
      <div className="tt-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="tt-dialog-header">
          <Icon name="link" size={20} className="text-primary" />
          <h3>הוסף קישור</h3>
        </div>
        <div className="tt-dialog-body">
          <label className="tt-dialog-label">כתובת URL</label>
          <input
            ref={inputRef}
            type="url"
            className="tt-dialog-input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com"
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleSave(); }
              if (e.key === 'Escape') onClose();
            }}
            dir="ltr"
          />
        </div>
        <div className="tt-dialog-footer">
          <button type="button" className="tt-dialog-btn-secondary" onClick={onClose}>
            ביטול
          </button>
          <button type="button" className="tt-dialog-btn-primary" onClick={handleSave} disabled={!url.trim()}>
            הוסף קישור
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Table dialog ──────────────────────────────────────────────────────────
const TableDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onInsert: (rows: number, cols: number) => void;
}> = ({ isOpen, onClose, onInsert }) => {
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);

  if (!isOpen) return null;

  return createPortal(
    <div className="tt-dialog-overlay" onClick={onClose}>
      <div className="tt-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="tt-dialog-header">
          <Icon name="table_chart" size={20} className="text-primary" />
          <h3>הוסף טבלה</h3>
        </div>
        <div className="tt-dialog-body">
          <div className="tt-dialog-grid">
            <div>
              <label className="tt-dialog-label">שורות</label>
              <input
                type="number"
                min="1"
                max="20"
                value={rows}
                className="tt-dialog-input"
                onChange={(e) => setRows(parseInt(e.target.value) || 1)}
              />
            </div>
            <div>
              <label className="tt-dialog-label">עמודות</label>
              <input
                type="number"
                min="1"
                max="20"
                value={cols}
                className="tt-dialog-input"
                onChange={(e) => setCols(parseInt(e.target.value) || 1)}
              />
            </div>
          </div>
        </div>
        <div className="tt-dialog-footer">
          <button type="button" className="tt-dialog-btn-secondary" onClick={onClose}>
            ביטול
          </button>
          <button
            type="button"
            className="tt-dialog-btn-primary"
            onClick={() => { onInsert(rows, cols); onClose(); }}
          >
            הוסף טבלה
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── MenuBar ───────────────────────────────────────────────────────────────
interface MenuBarProps {
  editor: Editor;
  onShowTableDialog: () => void;
  onShowLinkDialog: () => void;
  onAddImage: () => void;
  onEmojiClick: React.MouseEventHandler;
  isCompact?: boolean;
  /** When provided, render a dedicated "insert quote link" button. */
  onInsertQuoteLink?: () => void;
}

const MenuBar: React.FC<MenuBarProps> = ({
  editor, onShowTableDialog, onShowLinkDialog, onAddImage, onEmojiClick, isCompact = false, onInsertQuoteLink,
}) => {
  const inTable = editor.isActive('table');
  const [expanded, setExpanded] = useState(false);

  if (isCompact) {
    return (
      <div className="tt-toolbar-mobile">
        <div className="tt-toolbar tt-toolbar-compact">
          {/* Most essential actions — always visible on mobile */}
          <TbButton icon="format_bold" title="מודגש" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} />
          <TbButton icon="format_italic" title="נטוי" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} />
          <TbButton icon="format_list_bulleted" title="רשימה" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} />
          <TbButton icon="link" title="קישור" onClick={onShowLinkDialog} active={editor.isActive('link')} />
          {onInsertQuoteLink && (
            <TbButton icon="request_quote" title="קישור להצעה" onClick={onInsertQuoteLink} />
          )}
          <TbButton icon="image" title="תמונה" onClick={onAddImage} />
          <TbDivider />
          <TbButton icon="undo" title="בטל" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} />
          {/* Expand toggle */}
          <button
            type="button"
            onClick={() => setExpanded(e => !e)}
            className={['tt-btn tt-btn-expand', expanded ? 'tt-btn-active' : ''].join(' ')}
            title={expanded ? 'הסתר פעולות נוספות' : 'פעולות נוספות'}
            aria-label={expanded ? 'הסתר' : 'עוד פעולות'}
            aria-expanded={expanded}
          >
            <Icon name={expanded ? 'expand_less' : 'more_horiz'} size={18} />
          </button>
        </div>

        {/* Secondary row — collapsible */}
        {expanded && (
          <div className="tt-toolbar tt-toolbar-compact tt-toolbar-secondary">
            <TbButton icon="format_underlined" title="קו תחתון" onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} />
            <TbButton icon="format_strikethrough" title="קו חוצה" onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} />
            <TbDivider />
            <TbButton icon="format_h1" title="כותרת 1" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} />
            <TbButton icon="format_h2" title="כותרת 2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} />
            <TbButton icon="format_h3" title="כותרת 3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} />
            <TbDivider />
            <TbButton icon="format_list_numbered" title="רשימה ממוספרת" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} />
            <TbButton icon="checklist" title="רשימת משימות" onClick={() => editor.chain().focus().toggleTaskList().run()} active={editor.isActive('taskList')} />
            <TbButton icon="format_quote" title="ציטוט" onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} />
            <TbDivider />
            <TbButton icon="format_ink_highlighter" title="הדגשה" onClick={() => editor.chain().focus().toggleHighlight({ color: '#fef08a' }).run()} active={editor.isActive('highlight')} />
            <TbButton icon="mood" title="אימוג'י" onClick={onEmojiClick} />
            <TbButton icon="table_chart" title="טבלה" onClick={onShowTableDialog} />
            <TbDivider />
            <TbButton icon="redo" title="חזור" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} />
            <TbButton icon="format_clear" title="נקה עיצוב" onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="tt-toolbar">
      <TbButton icon="format_bold" title="מודגש (Ctrl+B)" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} />
      <TbButton icon="format_italic" title="נטוי (Ctrl+I)" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} />
      <TbButton icon="format_underlined" title="קו תחתון (Ctrl+U)" onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} />
      <TbButton icon="format_strikethrough" title="קו חוצה" onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} />

      <TbDivider />

      <TbButton icon="format_h1" title="כותרת 1" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} />
      <TbButton icon="format_h2" title="כותרת 2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} />
      <TbButton icon="format_h3" title="כותרת 3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} />

      <TbDivider />

      <TbButton icon="format_list_bulleted" title="רשימה" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} />
      <TbButton icon="format_list_numbered" title="רשימה ממוספרת" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} />
      <TbButton icon="checklist" title="רשימת משימות" onClick={() => editor.chain().focus().toggleTaskList().run()} active={editor.isActive('taskList')} />
      <TbButton icon="format_quote" title="ציטוט" onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} />
      <TbButton icon="code" title="בלוק קוד" onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} />

      <TbDivider />

      <TbButton icon="format_align_right" title="ישור לימין" onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} />
      <TbButton icon="format_align_center" title="ישור למרכז" onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} />
      <TbButton icon="format_align_left" title="ישור לשמאל" onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} />

      <TbDivider />

      <div className="tt-color-wrap" title="צבע טקסט">
        <Icon name="format_color_text" size={18} />
        <input
          type="color"
          className="tt-color-input"
          onInput={(e) => editor.chain().focus().setColor((e.target as HTMLInputElement).value).run()}
          value={editor.getAttributes('textStyle').color || '#1e293b'}
          aria-label="צבע טקסט"
        />
      </div>
      <TbButton icon="format_ink_highlighter" title="הדגשה" onClick={() => editor.chain().focus().toggleHighlight({ color: '#fef08a' }).run()} active={editor.isActive('highlight')} />

      <TbDivider />

      <TbButton icon="link" title="הוסף קישור" onClick={onShowLinkDialog} active={editor.isActive('link')} />
      {editor.isActive('link') && (
        <TbButton icon="link_off" title="הסר קישור" onClick={() => editor.chain().focus().unsetLink().run()} />
      )}
      {onInsertQuoteLink && (
        <TbButton icon="request_quote" title="קישור להצעה" onClick={onInsertQuoteLink} />
      )}
      <TbButton icon="image" title="הוסף תמונה" onClick={onAddImage} />
      <TbButton icon="table_chart" title="הוסף טבלה" onClick={onShowTableDialog} />
      <TbButton icon="mood" title="אימוג'י" onClick={onEmojiClick} />

      {inTable && (
        <>
          <TbDivider />
          <TbButton icon="view_column" title="הוסף עמודה" onClick={() => editor.chain().focus().addColumnAfter().run()} />
          <TbButton icon="table_rows" title="הוסף שורה" onClick={() => editor.chain().focus().addRowAfter().run()} />
          <TbButton icon="delete" title="מחק טבלה" onClick={() => editor.chain().focus().deleteTable().run()} />
        </>
      )}

      <TbDivider />

      <TbButton icon="undo" title="בטל (Ctrl+Z)" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} />
      <TbButton icon="redo" title="חזור (Ctrl+Shift+Z)" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} />

      <TbDivider />

      <TbButton icon="format_clear" title="נקה עיצוב" onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} />
    </div>
  );
};

// ─── Status bar ────────────────────────────────────────────────────────────
const StatusBar: React.FC<{
  editor: Editor;
  savedAt?: Date | null;
  showCharCount: boolean;
  showSaved: boolean;
}> = ({ editor, savedAt, showCharCount, showSaved }) => {
  const chars = editor.storage.characterCount?.characters() ?? 0;
  const words = editor.storage.characterCount?.words() ?? 0;

  const savedLabel = useMemo(() => {
    if (!savedAt) return null;
    const now = new Date();
    const diff = Math.floor((now.getTime() - savedAt.getTime()) / 1000);
    if (diff < 5) return 'נשמר עכשיו';
    if (diff < 60) return `נשמר לפני ${diff} שניות`;
    if (diff < 3600) return `נשמר לפני ${Math.floor(diff / 60)} דקות`;
    return `נשמר ב-${savedAt.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`;
  }, [savedAt]);

  if (!showCharCount && !showSaved) return null;

  return (
    <div className="tt-status-bar">
      {showSaved && savedLabel && (
        <div className="tt-status-saved">
          <Icon name="cloud_done" size={14} />
          <span>{savedLabel}</span>
        </div>
      )}
      {showCharCount && (
        <div className="tt-status-count">
          <span>{words} מילים</span>
          <span className="tt-status-dot">·</span>
          <span>{chars} תווים</span>
        </div>
      )}
    </div>
  );
};

// ─── Main editor ───────────────────────────────────────────────────────────
const TipTapEditor: React.FC<TipTapEditorProps> = ({
  value,
  onChange,
  placeholder = 'התחל לכתוב... הקלד "/" לפעולות מהירות',
  height = 300,
  showCharCount = false,
  showSavedIndicator = false,
  savedAt = null,
  quoteLink,
}) => {
  const toast = useToast();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [textDirection, setTextDirection] = useState<'ltr' | 'rtl'>('rtl');
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiAnchor, setEmojiAnchor] = useState<DOMRect | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const dragCounter = useRef(0);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bubbleMenuRef = useRef<HTMLDivElement>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  // Emoji anchor handler
  const handleEmojiClick = (e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setEmojiAnchor(rect);
    setShowEmojiPicker(true);
  };

  // Upload handler — try server, fallback to base64
  const uploadAndInsert = useCallback(async (file: File, insertFn: (url: string) => void) => {
    if (!file.type.startsWith('image/')) return;
    setUploading(true);
    try {
      const url = await uploadImageToServer(file);
      insertFn(url);
    } catch (err) {
      console.error('Image upload failed, falling back to base64:', err);
      toast.error('שגיאה בהעלאת התמונה לשרת — הוטמעה מקומית');
      try {
        const base64 = await fileToBase64(file);
        insertFn(base64);
      } catch {
        toast.error('שגיאה בטעינת התמונה');
      }
    } finally {
      setUploading(false);
    }
  }, [toast]);

  const extensions = useMemo(() => [
    StarterKit.configure({
      codeBlock: false, // use lowlight version instead
    }),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    Underline.configure({ HTMLAttributes: { class: 'underline' } }),
    Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: 'tiptap-link' } }),
    TextStyle,
    Color,
    Highlight.configure({ multicolor: true }),
    Table.configure({ resizable: true }),
    TableRow,
    TableCell,
    TableHeader,
    TaskList.configure({ HTMLAttributes: { class: 'tiptap-task-list' } }),
    TaskItem.configure({ nested: true, HTMLAttributes: { class: 'tiptap-task-item' } }),
    Image.configure({ inline: false, allowBase64: true, HTMLAttributes: { class: 'tiptap-image' } }),
    Youtube.configure({ controls: true, nocookie: true, HTMLAttributes: { class: 'tiptap-youtube' } }),
    CharacterCount,
    CodeBlockLowlight.configure({ lowlight }),
    Mention.configure({
      HTMLAttributes: { class: 'tt-mention-chip' },
      renderText({ node }) {
        return `@${node.attrs.label}`;
      },
      suggestion: mentionSuggestion,
    }),
    Placeholder.configure({ placeholder, includeChildren: true }),
  ], [placeholder]);

  const editor = useEditor({
    extensions: [
      ...extensions,
      SlashCommand({
        onImage: () => fileInputRef.current?.click(),
        onLink: () => setShowLinkDialog(true),
        onTable: () => setShowTableDialog(true),
        onEmoji: () => {
          const sel = window.getSelection();
          const range = sel?.rangeCount ? sel.getRangeAt(0) : null;
          const rect = range?.getBoundingClientRect() || null;
          setEmojiAnchor(rect);
          setShowEmojiPicker(true);
        },
      }),
    ],
    content: value || '',
    onUpdate: ({ editor: editorInstance }) => {
      const html = editorInstance.getHTML();
      onChange(html);
      const plainText = editorInstance.getText();
      const hebrewRegex = /[\u0590-\u05FF]/;
      const newDirection = hebrewRegex.test(plainText) ? 'rtl' : 'ltr';
      if (newDirection !== textDirection) setTextDirection(newDirection);
    },
    editorProps: {
      attributes: { class: 'tiptap-editor-content', dir: 'rtl' },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of Array.from(items)) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (file) {
              uploadAndInsert(file, (url) => {
                view.dispatch(
                  view.state.tr.replaceSelectionWith(
                    view.state.schema.nodes.image.create({ src: url })
                  )
                );
              });
            }
            return true;
          }
        }
        return false;
      },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;
        const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
        if (imageFiles.length === 0) return false;

        event.preventDefault();
        const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });

        imageFiles.forEach((file) => {
          uploadAndInsert(file, (url) => {
            const { tr } = view.state;
            if (pos) {
              view.dispatch(tr.insert(pos.pos, view.state.schema.nodes.image.create({ src: url })));
            } else {
              view.dispatch(tr.replaceSelectionWith(view.state.schema.nodes.image.create({ src: url })));
            }
          });
        });
        return true;
      },
    },
  });

  // Sync external value
  useEffect(() => {
    if (!editor) return;
    if (!value || value === '' || value === '<p></p>' || value === '<p><br></p>') {
      const currentHTML = editor.getHTML();
      if (currentHTML !== '<p></p>') editor.commands.setContent('');
      return;
    }
    const currentHTML = editor.getHTML();
    if (value !== currentHTML) editor.commands.setContent(value);
  }, [editor, value]);

  // Direction
  useEffect(() => {
    if (!editor) return;
    const timer = setTimeout(() => {
      try {
        editor.view?.dom?.setAttribute('dir', textDirection);
      } catch { /* ignore */ }
    }, 0);
    return () => clearTimeout(timer);
  }, [editor, textDirection]);

  // Register BubbleMenu plugin once editor + ref are ready
  useEffect(() => {
    if (!editor || !bubbleMenuRef.current) return;
    const menuElement = bubbleMenuRef.current;
    menuElement.style.display = 'flex';

    const plugin = BubbleMenuPlugin({
      editor,
      element: menuElement,
      pluginKey: 'bubbleMenu',
      shouldShow: ({ editor, from, to, state }) => {
        if (from === to) return false;
        if (editor.isActive('image') || editor.isActive('codeBlock') || editor.isActive('table')) return false;
        const slice = state.doc.textBetween(from, to).trim();
        return slice.length > 0;
      },
    });

    editor.registerPlugin(plugin);

    return () => {
      try {
        editor.unregisterPlugin('bubbleMenu');
      } catch { /* editor may be destroyed */ }
    };
  }, [editor]);

  // Transplant ProseMirror DOM between normal wrapper and fullscreen mount.
  // This keeps the Editor instance + its plugins (slash, mention, bubble menu)
  // fully intact while we change which wrapper surrounds it visually.
  useEffect(() => {
    if (!editor) return;
    const proseMirrorDom = editor.view.dom as HTMLElement;
    if (!proseMirrorDom) return;

    if (isFullscreen) {
      const mount = document.getElementById('tt-fullscreen-mount');
      if (mount && proseMirrorDom.parentElement !== mount) {
        mount.appendChild(proseMirrorDom);
      }
    } else {
      // Put it back into the normal container
      const normal = editorContainerRef.current;
      if (normal && proseMirrorDom.parentElement !== normal) {
        // Insert before the drag overlay / upload indicator divs so visual order is preserved
        normal.insertBefore(proseMirrorDom, normal.firstChild);
      }
    }
  }, [editor, isFullscreen]);

  // Fullscreen keys
  useEffect(() => {
    if (!isFullscreen) return;
    const handle = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsFullscreen(false); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [isFullscreen]);

  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isFullscreen]);

  // Drag overlay handlers
  const hasImageFile = (e: React.DragEvent): boolean => {
    if (!e.dataTransfer) return false;
    if (e.dataTransfer.items) {
      for (const item of Array.from(e.dataTransfer.items)) {
        if (item.kind === 'file' && item.type.startsWith('image/')) return true;
      }
    }
    return !!e.dataTransfer.types?.includes('Files');
  };

  const handleDragEnter = (e: React.DragEvent) => {
    if (!hasImageFile(e)) return;
    e.preventDefault();
    dragCounter.current += 1;
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!hasImageFile(e)) return;
    e.preventDefault();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };

  const handleDrop = () => {
    dragCounter.current = 0;
    setIsDragging(false);
  };

  const handleInsertTable = (rows: number, cols: number) => {
    if (editor) editor.chain().focus().insertTable({ rows, cols }).run();
  };

  const handleSaveLink = (url: string) => {
    if (editor) editor.chain().focus().setLink({ href: url }).run();
  };

  // Insert the quote's public link as an anchor at the cursor. The label is a
  // normal text node wrapped in a Link mark (the sanitizer allows anchors).
  const handleInsertQuoteLink = useCallback(() => {
    if (!editor || !quoteLink) return;
    const label = quoteLink.label || 'צפייה בהצעת המחיר';
    editor
      .chain()
      .focus()
      .insertContent({
        type: 'text',
        text: label,
        marks: [{ type: 'link', attrs: { href: quoteLink.url, target: '_blank', rel: 'noopener noreferrer' } }],
      })
      // Drop the link mark so text typed after the anchor isn't part of it.
      .insertContent(' ')
      .unsetMark('link')
      .run();
  }, [editor, quoteLink]);

  const handleAddImage = () => fileInputRef.current?.click();

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && editor) {
      await uploadAndInsert(file, (url) => {
        editor.chain().focus().setImage({ src: url }).run();
      });
      e.target.value = '';
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen((p) => !p);
    setTimeout(() => { if (editor) editor.commands.focus('end'); }, 50);
  };

  const getLinkUrl = () => editor?.getAttributes('link').href || '';

  const handleEmojiSelect = (emoji: string) => {
    if (editor) editor.chain().focus().insertContent(emoji).run();
  };

  if (!editor) return null;

  const dragOverlay = isDragging && (
    <div className="tt-drag-overlay" aria-hidden="true">
      <div className="tt-drag-overlay-card">
        <Icon name="add_photo_alternate" size={48} className="text-primary" />
        <div className="tt-drag-overlay-title">שחרר כדי להוסיף תמונה</div>
        <div className="tt-drag-overlay-hint">ניתן להוסיף מספר תמונות בו-זמנית</div>
      </div>
    </div>
  );

  const uploadingOverlay = uploading && (
    <div className="tt-upload-indicator" aria-live="polite">
      <Icon name="progress_activity" size={16} className="animate-spin" />
      <span>מעלה תמונה...</span>
    </div>
  );

  // Bubble menu DOM (the BubbleMenuExtension in `extensions` wires it to selection)
  const bubbleMenu = (
    <div ref={bubbleMenuRef} className="tt-bubble-menu" style={{ display: 'none' }}>
      <TbButton icon="format_bold" title="מודגש" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} />
      <TbButton icon="format_italic" title="נטוי" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} />
      <TbButton icon="format_underlined" title="קו תחתון" onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} />
      <TbButton icon="format_strikethrough" title="קו חוצה" onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} />
      <TbDivider />
      <TbButton icon="format_ink_highlighter" title="הדגשה" onClick={() => editor.chain().focus().toggleHighlight({ color: '#fef08a' }).run()} active={editor.isActive('highlight')} />
      <TbButton icon="link" title="קישור" onClick={() => setShowLinkDialog(true)} active={editor.isActive('link')} />
    </div>
  );

  return (
    <>
      {/* Normal mode OR fullscreen "dock" — EditorContent lives here permanently.
          In fullscreen we visually hide this wrapper with CSS but keep it in the
          React tree so ProseMirror DOM + plugin attachments stay alive. The
          fullscreen overlay renders BELOW and we move ProseMirror into it via
          a DOM transplant effect. See useEffect on isFullscreen below. */}
      <div
        className="tt-wrapper"
        style={{ height: height + 52 + (showCharCount || showSavedIndicator ? 32 : 0) }}
        hidden={isFullscreen}
      >
        <MenuBar
          editor={editor}
          onShowTableDialog={() => setShowTableDialog(true)}
          onShowLinkDialog={() => setShowLinkDialog(true)}
          onAddImage={handleAddImage}
          onEmojiClick={handleEmojiClick}
          isCompact={isMobile}
          onInsertQuoteLink={quoteLink ? handleInsertQuoteLink : undefined}
        />
        <div
          ref={editorContainerRef}
          className="tt-editor-container"
          style={{ height }}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={(e) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.ProseMirror') || target === editorContainerRef.current) {
              editor.commands.focus('end');
            }
          }}
        >
          <EditorContent editor={editor} />
          {dragOverlay}
          {uploadingOverlay}
        </div>
        <StatusBar editor={editor} savedAt={savedAt} showCharCount={showCharCount} showSaved={showSavedIndicator} />
        <button
          className="tt-fullscreen-toggle"
          onClick={toggleFullscreen}
          type="button"
          title="מסך מלא"
          aria-label="מסך מלא"
        >
          <Icon name="open_in_full" size={16} />
        </button>
      </div>

      {/* Fullscreen overlay — a shell WITHOUT EditorContent. The ProseMirror
          element is moved into `#tt-fullscreen-mount` by a useEffect. */}
      {isFullscreen && createPortal(
        <div className="tt-fullscreen-overlay">
          <div className="tt-fullscreen-header">
            <div className="flex items-center gap-2">
              <Icon name="edit_note" size={22} className="text-primary" />
              <h3>עריכת טקסט במסך מלא</h3>
            </div>
            <button className="tt-fullscreen-close" onClick={toggleFullscreen} type="button" aria-label="סגור">
              <Icon name="close" size={20} />
            </button>
          </div>
          <MenuBar
            editor={editor}
            onShowTableDialog={() => setShowTableDialog(true)}
            onShowLinkDialog={() => setShowLinkDialog(true)}
            onAddImage={handleAddImage}
            onEmojiClick={handleEmojiClick}
            onInsertQuoteLink={quoteLink ? handleInsertQuoteLink : undefined}
          />
          <div
            ref={fullscreenContainerRef}
            id="tt-fullscreen-mount"
            className="tt-editor-container tt-fullscreen-editor-container"
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={(e) => {
              const target = e.target as HTMLElement;
              if (!target.closest('.ProseMirror') || target === fullscreenContainerRef.current) {
                editor.commands.focus('end');
              }
            }}
          />
          <StatusBar editor={editor} savedAt={savedAt} showCharCount={showCharCount} showSaved={showSavedIndicator} />
          <div className="tt-fullscreen-footer">
            <button className="tt-fullscreen-done" onClick={toggleFullscreen} type="button">סיום</button>
          </div>
        </div>,
        document.body
      )}

      {bubbleMenu}

      <TableDialog isOpen={showTableDialog} onClose={() => setShowTableDialog(false)} onInsert={handleInsertTable} />
      <LinkDialog open={showLinkDialog} initialUrl={getLinkUrl()} onClose={() => setShowLinkDialog(false)} onSave={handleSaveLink} />
      <EmojiPicker
        open={showEmojiPicker}
        onClose={() => setShowEmojiPicker(false)}
        onSelect={handleEmojiSelect}
        anchorRect={emojiAnchor}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
    </>
  );
};

export default React.memo(TipTapEditor);
